import { MultiselectOption, SiteInfo } from '@cbo/shared-library';
import { useTheme, Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DebouncedFunc, isEqual } from 'lodash';
import { FixedSizeList } from 'react-window';
import { useSites } from '../../../contexts/siteContext';
import FilterContainer from '../FilterFrame/FilterFrame';
import SearchBar from '../SearchBar/SearchBar';
import SiteFilterButton from '../SiteFilter/SiteFilterButton';
import TabBar, { getUniqueTabs } from '../TabBar/TabBar';
import { ButtonSize, LabelGlobalFilterProps } from '../types';
import MultiselectButton from './MultiselectButton';
import MultiselectTextField from './MultiselectTextField';
import { getSiteInfo, isMultiselectOptionMatch } from '../../../utils/reportingUtils/filterUtils';
import { truncateString } from '../../../utils';

declare type truncateOptionType = {
  isTruncateSubText?: boolean;
  subTextMaxLength?: number;
};
interface RowProps {
  index: number;
  style: React.CSSProperties;
  optionItems: MultiselectOption[];
  theme: Theme;
  selected: MultiselectOption[];
  setSelected: React.Dispatch<React.SetStateAction<MultiselectOption[]>>;
  truncateOption?: truncateOptionType;
}
function Row({ index, style, optionItems, theme, selected, setSelected, truncateOption }: RowProps) {
  const [hoveredOption, setHoveredOption] = useState<string>('');

  const handleButtonStyling = (option: string) => {
    if (option === hoveredOption) {
      return { display: 'block' };
    }
    return { display: 'none' };
  };

  const handleOnlyButton = (event: React.MouseEvent<HTMLButtonElement>, option: MultiselectOption) => {
    event.stopPropagation();
    const currentItem = [{ label: option.label, subtext: option.subtext, id: option.id }];
    setSelected(currentItem);
  };

  const handleRowClick = (event: React.MouseEvent<HTMLElement>, option: MultiselectOption) => {
    let currentSelected: MultiselectOption[] = [];
    if (selected.some((opt: MultiselectOption) => isMultiselectOptionMatch(opt, option))) {
      currentSelected = selected.filter((id: MultiselectOption) => !isMultiselectOptionMatch(id, option));
    } else {
      currentSelected = selected.concat({
        label: option.label,
        subtext: option.subtext,
        id: option.id,
      });
    }
    setSelected(currentSelected);
  };

  const option = optionItems[index];
  let subText = '';
  if (option?.subtext) {
    if (truncateOption?.isTruncateSubText && truncateOption?.subTextMaxLength)
      subText = truncateString(option.subtext, truncateOption.subTextMaxLength);
    else subText = option.subtext;
  }
  return (
    <MenuItem
      sx={{
        '&.Mui-focusVisible': {
          background: 'none',
        },
        borderRadius: 1,
        padding: '8px 16px',
        marginLeft: '8px',
        marginRight: '8px',
        height: '60px',
        maxWidth: 'calc(100% - 16px)',
      }}
      tabIndex={-1}
      key={`${option.label}-${option.subtext}`}
      onClick={(e) => handleRowClick(e, option)}
      data-testid={`${option.label}-row`}
      onMouseEnter={() => {
        setHoveredOption(option.label);
      }}
      onMouseLeave={() => {
        setHoveredOption('');
      }}
      onFocusVisible={() => {
        setHoveredOption(option.label);
      }}
      style={style}
    >
      <FormControlLabel
        sx={{ maxWidth: '263px', textWrap: 'wrap' }}
        control={
          <Checkbox
            data-testid={`${option.label}${option.subtext ? `-${option.subtext}` : ''}-checkbox`}
            checked={selected.some((id: MultiselectOption) => isMultiselectOptionMatch(id, option))}
            style={{ pointerEvents: 'auto' }}
            value={option.label}
            sx={{
              '&.Mui-focusVisible': {
                backgroundColor: theme.palette.mode === 'dark' ? '#163349' : '#eaf5fe',
              },
            }}
          />
        }
        label={
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography data-testid={`${option.label}-body1`} variant='body1'>
              {option.label}
            </Typography>
            <Typography data-testid={`${option.label}-body2`} variant='body2' color={theme.palette.text.secondary}>
              {subText}
            </Typography>
          </Box>
        }
        style={{ pointerEvents: 'none' }}
      />
      <Button
        onClick={(e) => handleOnlyButton(e, option)}
        style={handleButtonStyling(option.label)}
        sx={{
          float: 'right',
          marginLeft: 'auto',
          '&:focus-visible': {
            boxShadow: 'none',
            color: theme.palette.primary.main,
            background: theme.palette.mode === 'dark' ? '#163349' : '#eaf5fe',
            outline: `2px solid ${theme.palette.primary.dark}`,
          },
        }}
        data-testid={`${option.label}-only`}
        aria-label={`${option.label}-only`}
      >
        <Typography variant='caption'>{useTranslation().t('globalFilters.multiselect.only')}</Typography>
      </Button>
    </MenuItem>
  );
}

/**
 * @param options The options to filter the data by
 * @param defaultChecked Boolean determining whether you want the default state of the filter to be checked
 * @param currentSite The default value for the site filter
 * @param useTabs Boolean determining whether the component is using tabs
 * @param loading Boolean determining whether the component is in a loading state
 * @param sortBy Function to sort the options in the multiselect
 * @param truncateOption the truncate prop to truncate overflow subtext
 * @param debouncedSearchFn Debounced function to handle search input changes to trigger a re-render from parent component when search value changes
 * @param isSearchable Boolean to determine whether to show the search bar based on the number of options by default or from API
 * @param isTextField Optional, whether to render as a text field
 * @param textFieldLabel Optional, the label for the text field
 * @param isRequired Optional, whether the field is required
 */
export interface MultiselectProps extends LabelGlobalFilterProps {
  options: MultiselectOption[];
  defaultChecked?: boolean;
  currentSite?: MultiselectOption[];
  useTabs?: boolean;
  loading?: boolean;
  sortBy?: (a: MultiselectOption, b: MultiselectOption) => number;
  fixedSizedListWidth?: number | string;
  truncateOption?: truncateOptionType;
  debouncedSearchFn?: DebouncedFunc<(searchValue: string | undefined) => void>;
  isSearchable?: boolean;
  isTextField?: boolean;
  textFieldLabel?: string;
  isRequired?: boolean;
}

function Multiselect({
  singular,
  plural,
  controllerName,
  options,
  defaultChecked,
  onlyFilterContainer,
  currentSite,
  buttonSize = ButtonSize.SMALL,
  useTabs,
  loading,
  sortBy,
  fixedSizedListWidth = 360,
  truncateOption,
  debouncedSearchFn,
  isSearchable,
  isTextField,
  textFieldLabel,
  isRequired,
}: MultiselectProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const form = useFormContext();
  const { control, resetField, setValue, getValues } = form;

  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | HTMLButtonElement | null>(null);

  let currentSiteInfo: SiteInfo[];
  if (currentSite) {
    currentSiteInfo = getSiteInfo(currentSite);
  }

  const [selected, setSelected] = useState<MultiselectOption[]>(getValues(controllerName));
  const [selectAllChecked, setSelectAllChecked] = useState(defaultChecked);
  const [resetEnabled, setResetEnabled] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const uniqueTabs: string[] = getUniqueTabs(options);
  const [tabValue, setTabValue] = useState<string>(uniqueTabs[0] || '');

  const { updateMultiSelectedSites, multiSelectedSites } = useSites();

  useEffect(() => {
    if (defaultChecked && options && !isEqual(selected, options)) {
      setSelected(options);
      setValue(controllerName, options, { shouldValidate: true });
      return;
    }
    const selectedFromNewOptions = selected.filter((id) =>
      options.some((option) => isMultiselectOptionMatch(id, option))
    );

    if (options && options.length > 0 && !isEqual(selected, selectedFromNewOptions)) {
      setSelected(selectedFromNewOptions);
      setValue(controllerName, selectedFromNewOptions, { shouldValidate: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChecked, options]);

  useEffect(() => {
    let selectedSites: SiteInfo[] = [];
    if (currentSite) {
      selectedSites = getSiteInfo(selected);
      updateMultiSelectedSites(selectedSites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, updateMultiSelectedSites]);

  const optionItems = useMemo(
    () =>
      options
        .filter((group) => (useTabs ? group.tab === tabValue : group))
        .filter((option) => option.label?.toLowerCase().indexOf(searchValue.toLowerCase()) > -1)
        .sort((a, b) => (sortBy ? sortBy(a, b) : 0)),
    [options, searchValue, sortBy, tabValue, useTabs]
  );

  let selectAllString = '';
  if (useTabs) {
    selectAllString = `${t('globalFilters.multiselect.selectAll')} ${tabValue.toLowerCase()} (${optionItems.length})`;
  } else if (optionItems.length === options.length) {
    selectAllString = `${t('globalFilters.multiselect.selectAll')} ${plural}`;
  } else {
    selectAllString = `${t('globalFilters.multiselect.selectAll')} results (${optionItems.length})`;
  }

  useEffect(() => {
    if (!anchorEl && currentSite && multiSelectedSites.length > 0) {
      const storedSites = multiSelectedSites.map((site) => ({
        label: site.name ?? '',
        subtext: site.referenceId ?? '',
        id: site.enterpriseUnitId ?? '',
      }));
      setValue(controllerName, storedSites, { shouldValidate: true });
      setSelected(storedSites);
    } else if (!anchorEl && currentSite && selected.length === 0) {
      setValue(controllerName, currentSite, { shouldValidate: true });
      setSelected(currentSite);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleClick = (event: React.MouseEvent<HTMLElement>) => {
    let currentSelected = selected.filter(
      (opt: MultiselectOption) => !optionItems.filter((id) => isMultiselectOptionMatch(id, opt)).length
    );
    if (selectAllChecked) {
      setSelected(currentSelected);
    } else {
      currentSelected = currentSelected.concat(optionItems);
      setSelected(currentSelected);
    }
  };

  const resetSiteSelection = useCallback(() => {
    setValue(controllerName, currentSite, { shouldValidate: true });
    setSelected(currentSite ?? []);
  }, [controllerName, currentSite, setValue]);

  const resetFormValues = useCallback(
    (isFullRest: boolean) => {
      if (isFullRest) {
        setValue(controllerName, [], { shouldValidate: true });
      } else {
        resetField(controllerName);
      }
    },
    [controllerName, resetField, setValue]
  );

  const resetToDefaultState = useCallback(() => {
    if (defaultChecked) {
      setSelected(options);
    } else {
      setSelected([]);
      // resetting search value and calling debounced function to trigger re-render from parent component
      if (debouncedSearchFn) debouncedSearchFn(undefined);
      setSearchValue('');
    }
  }, [defaultChecked, options, debouncedSearchFn]);

  const handleReset = useCallback(
    (isFullRest = false) => {
      setSelectAllChecked(defaultChecked);
      if (currentSite) {
        resetSiteSelection();
      } else {
        resetFormValues(isFullRest);
        resetToDefaultState();
      }
      if (useTabs) setTabValue(uniqueTabs[0] || '');
    },
    [defaultChecked, currentSite, useTabs, uniqueTabs, resetSiteSelection, resetFormValues, resetToDefaultState]
  );

  const handleClose = () => {
    setAnchorEl(null);
    if (selected.length === 0 && defaultChecked) {
      setSelected(options);
      setValue(controllerName, options, { shouldValidate: true });
    } else {
      setValue(controllerName, selected, { shouldValidate: true });
    }

    if (selected.length === 0 && currentSite) {
      setValue(controllerName, currentSite, { shouldValidate: true });
      setSelected(currentSite);
    }
    if (selected.length === 0) {
      if (debouncedSearchFn) debouncedSearchFn(undefined);
    }
    if (currentSite || selected.length === 0) setSearchValue('');
  };

  const siteButton = useMemo(
    () => (
      <SiteFilterButton
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        selected={multiSelectedSites}
        optionsLength={options.length}
        currentSite={currentSiteInfo}
        resetEnabled={resetEnabled}
        buttonSize={buttonSize}
        handleReset={handleReset}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, resetEnabled, anchorEl, buttonSize, handleReset, options.length, currentSite, multiSelectedSites]
  );

  const standardButton = useMemo(
    () => (
      <MultiselectButton
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        controllerName={controllerName}
        defaultChecked={defaultChecked}
        optionsLength={options.length}
        pluralName={plural}
        singularName={singular}
        selected={selected}
        buttonSize={buttonSize}
        handleReset={handleReset}
      />
    ),
    [anchorEl, controllerName, defaultChecked, options.length, plural, singular, selected, buttonSize, handleReset]
  );

  const standardTextField = useMemo(
    () => (
      <MultiselectTextField
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        controllerName={controllerName}
        optionsLength={options.length}
        pluralName={plural}
        selected={selected}
        handleReset={handleReset}
        resetEnabled={resetEnabled}
        textFieldLabel={textFieldLabel}
        isRequired={isRequired}
      />
    ),
    [anchorEl, controllerName, options.length, plural, selected, handleReset, resetEnabled, textFieldLabel, isRequired]
  );

  useEffect(() => {
    if (defaultChecked ? selected.length === options.length : selected.length === 0) {
      setResetEnabled(currentSite !== undefined && selected.length === 0);
    } else {
      setResetEnabled(currentSite === undefined || selected.length !== 1 || selected[0].label !== currentSite[0].label);
    }
    if (
      optionItems.length &&
      optionItems.every((opt) => selected.some((value: MultiselectOption) => isMultiselectOptionMatch(value, opt)))
    ) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  }, [defaultChecked, selected, options, optionItems, currentSite]);
  const multiSelect = (
    <Controller
      name={controllerName}
      control={control}
      render={({ field }) => (
        <Box data-testid={`${controllerName}-multiselect`}>
          <Box aria-label='multiselect-popup' data-testid='multiselect-popup'>
            {useTabs && <TabBar activeTab={tabValue} tabs={uniqueTabs} setActiveTab={setTabValue} />}
            {(options.filter((group) => (useTabs ? group.tab === tabValue : group)).length >= 10 || isSearchable) && (
              <SearchBar
                showClearIcon={searchValue.length > 0}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                debouncedSearchFn={debouncedSearchFn}
              />
            )}
            <Box>
              <MenuItem
                sx={{
                  borderRadius: 1,
                  padding: '8px 16px',
                  marginLeft: '8px',
                  marginRight: '8px',
                  height: '44px',
                  '&.Mui-focusVisible': {
                    background: 'none',
                  },
                }}
                tabIndex={-1}
                onClick={handleToggleClick}
              >
                {loading ? (
                  <Box
                    data-testid='switch-loading'
                    sx={{ display: 'flex', flexDirection: 'column', width: '210px', borderRadius: '8px' }}
                  >
                    <Skeleton variant='rectangular' height={25} sx={{ borderRadius: '8px' }} />
                  </Box>
                ) : (
                  <FormControlLabel
                    control={
                      <Switch
                        sx={{ paddingLeft: '8px' }}
                        checked={selectAllChecked}
                        data-testid='multiselect-select-all'
                        aria-label={t('globalFilters.multiselect.selectAll')}
                        style={{ pointerEvents: 'auto' }}
                      />
                    }
                    label={selectAllString}
                    labelPlacement='start'
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </MenuItem>
              {loading ? (
                <div
                  data-testid='skeleton-loading-options'
                  style={{
                    borderRadius: '8px',
                    padding: '8px 16px',
                    marginLeft: '8px',
                    marginRight: '8px',
                    gap: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {[...Array(7).keys()].map((i) => (
                    <FormControlLabel
                      key={i}
                      sx={{ maxWidth: '263px', textWrap: 'wrap' }}
                      control={<Checkbox data-testid='-checkbox' style={{ pointerEvents: 'auto' }} disabled />}
                      label={
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '263px', borderRadius: '8px' }}>
                          <Skeleton variant='rectangular' height={25} sx={{ borderRadius: '8px' }} />
                        </Box>
                      }
                      style={{ pointerEvents: 'none' }}
                    />
                  ))}
                </div>
              ) : (
                <FixedSizeList
                  height={Math.min(optionItems.length * 60, 360)}
                  width={fixedSizedListWidth}
                  itemCount={optionItems.length}
                  itemSize={60}
                >
                  {({ index, style }) => (
                    <Row
                      index={index}
                      style={style}
                      optionItems={optionItems}
                      theme={theme}
                      selected={selected}
                      setSelected={setSelected}
                      truncateOption={truncateOption}
                    />
                  )}
                </FixedSizeList>
              )}
            </Box>
          </Box>
        </Box>
      )}
    />
  );

  return onlyFilterContainer ? (
    multiSelect
  ) : (
    <FilterContainer
      resetEnabled={resetEnabled}
      handleClose={handleClose}
      anchorEl={anchorEl}
      button={currentSite ? siteButton : standardButton}
      textField={isTextField && standardTextField}
      title={singular}
      body={multiSelect}
      resetFunction={handleReset}
    />
  );
}

Row.defaultProps = {
  truncateOption: undefined,
};

Multiselect.defaultProps = {
  defaultChecked: true,
  currentSite: undefined,
  useTabs: false,
  loading: undefined,
  sortBy: undefined,
  fixedSizedListWidth: 360,
  truncateOption: undefined,
  debouncedSearchFn: undefined,
  isSearchable: false,
  isTextField: false,
  textFieldLabel: '',
  isRequired: false,
};

export default Multiselect;
